import { get } from "lodash";
import { useGrowl } from "PFApp/use_growl";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { Typography } from "PFComponents/typography";
import {
  useCreateCustomValueByType,
  useCustomValueCreateError,
  useCustomValuesInvalidate,
  useCustomValueUpdate
} from "PFCore/hooks/queries/custom_fields/admin";
import { CustomValue } from "PFTypes";
import React, { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useSkillsCustomTypeId } from "../../../utils/use_skills_custom_type";
import css from "../skills_management_modal.module.scss";

type FormValues = {
  skillName: string;
  skillDescription: string;
};

type EditSkillModalProps = {
  onClose: VoidFunction;
  skill?: CustomValue;
};

export const EditSkillModal = ({ onClose, skill }: EditSkillModalProps): JSX.Element => {
  const { t } = useTranslation("skillsManagement", { keyPrefix: "header.modals" });
  const skillsCustomTypeId = useSkillsCustomTypeId();
  const { parseCustomValueCreateError } = useCustomValueCreateError();
  const growl = useGrowl();

  const growlSuccessMessage = useCallback(
    (skillName: string) =>
      growl({
        message: t(`successMessage.${skill ? "update" : "create"}`, { skillName })
      }),
    [growl, skill, t]
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm<FormValues>({
    defaultValues: {
      skillName: skill ? String(skill.value) : "",
      skillDescription: skill?.description || ""
    }
  });

  const { mutateAsync: createCustomValue } = useCreateCustomValueByType();
  const { mutateAsync: updateCustomValue } = useCustomValueUpdate();
  const { invalidate } = useCustomValuesInvalidate();

  const handleOK = useCallback(() => {
    const actionFn = ({ skillName: value, skillDescription }: FormValues) => {
      const payload: { value: string; description?: string } = { value };

      if (skillDescription) {
        payload.description = skillDescription;
      }

      return skill
        ? updateCustomValue({
            customTypeId: skillsCustomTypeId,
            customValueId: skill.id,
            payload
          })
        : createCustomValue({ customTypeId: skillsCustomTypeId, payload });
    };

    // The promise is needed to close modal with animation after the mutation is done
    return new Promise<void>((resolve) => {
      handleSubmit((values: FormValues) =>
        actionFn(values)
          .then((data) => {
            invalidate();
            growlSuccessMessage(data.value);
            resolve();
          })
          .catch((err) => {
            parseCustomValueCreateError(err, (message) => {
              setError("skillName", { message });
            });
          })
      )();
    });
  }, [
    createCustomValue,
    updateCustomValue,
    growlSuccessMessage,
    handleSubmit,
    parseCustomValueCreateError,
    setError,
    skillsCustomTypeId,
    skill,
    invalidate
  ]);

  return (
    <ConfirmModal handleOK={handleOK} handleClose={onClose} labelOK={t("save")}>
      <Typography variant="h3" className={css.title}>
        {t("edit.title")}
      </Typography>
      <form>
        <Controller
          name="skillName"
          control={control}
          rules={{
            required: t("errors.required")
          }}
          render={({ field }) => (
            <InputFieldSet
              label={t("edit.enterSkillName")}
              name={field.name}
              value={field.value}
              error={get(errors, field.name)?.message}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
        <Controller
          name="skillDescription"
          control={control}
          render={({ field }) => (
            <InputFieldSet
              inputType="textarea"
              label={t("edit.enterSkillDescription")}
              name={field.name}
              value={field.value}
              error={get(errors, field.name)?.message}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
      </form>
    </ConfirmModal>
  );
};
